import { useState, useEffect, useCallback } from "react";
import { apiGetDashboardUsage } from "../../services/api";
import { formatExpiryDate } from "../../utils/formatters";
import styles from "./DashboardPage.module.css";

export default function DashboardPage({ user, products }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await apiGetDashboardUsage(user.token);
      setData(result);
      setError("");
      // Default to current month
      if (!selectedMonth && result.months?.length > 0) {
        setSelectedMonth(result.months[0].month);
      }
    } catch (err) {
      setError("Failed to load dashboard data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user.token, selectedMonth]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  const months = data?.months || [];
  const items = data?.items || [];
  const currentMonth = months[0] || { drugs: 0, supplies: 0, total: 0 };
  const filteredItems = selectedMonth
    ? items.filter(i => i.month === selectedMonth)
    : items;

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  // Expiry alerts: items expiring within 3 months
  const now = new Date();
  const threeMonthsOut = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
  const expiringItems = (products || [])
    .filter(p => p.expiryDate && new Date(p.expiryDate + 'T00:00:00') <= threeMonthsOut)
    .sort((a, b) => new Date(a.expiryDate + 'T00:00:00') - new Date(b.expiryDate + 'T00:00:00'));

  // Reorder alerts: items within 10% of min order line
  const reorderItems = (products || [])
    .filter(p => p.minOrderLine != null && p.minOrderLine > 0 && p.stock <= p.minOrderLine * 1.1)
    .sort((a, b) => (a.stock / a.minOrderLine) - (b.stock / b.minOrderLine));

  const getDaysUntilExpiry = (expiryDate) => {
    const diff = new Date(expiryDate + 'T00:00:00') - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getExpiryClass = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days <= 30) return styles.alertDanger;
    return styles.alertWarn;
  };

  const getStockClass = (stock, minOrderLine) => {
    if (stock <= minOrderLine) return styles.alertDanger;
    return styles.alertWarn;
  };

  return (
    <div className={styles.container}>
      <div className={styles.heading}>Usage Dashboard</div>

      {/* Expiry alerts */}
      <div className={styles.alertSection}>
        <div className={styles.sectionTitle}>
          Expiring Soon
          {expiringItems.length > 0 && (
            <span className={styles.alertBadgeDanger}>{expiringItems.length}</span>
          )}
        </div>
        {expiringItems.length === 0 ? (
          <div className={styles.empty}>No items expiring within 3 months.</div>
        ) : (
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span className={styles.colItem}>Item</span>
              <span className={styles.colType}>Type</span>
              <span className={styles.colExpiry}>Expiry Date</span>
              <span className={styles.colDays}>Days Left</span>
            </div>
            {expiringItems.map(p => {
              const days = getDaysUntilExpiry(p.expiryDate);
              return (
                <div key={p.id} className={styles.tableRow}>
                  <span className={styles.colItem}>{p.description}</span>
                  <span className={styles.colType}>
                    <span className={p.type === "Drug" ? styles.tagDrug : styles.tagSupply}>{p.type}</span>
                  </span>
                  <span className={`${styles.colExpiry} ${getExpiryClass(p.expiryDate)}`}>
                    {formatExpiryDate(p.expiryDate)}
                  </span>
                  <span className={`${styles.colDays} ${getExpiryClass(p.expiryDate)}`}>
                    {days <= 0 ? "EXPIRED" : `${days}d`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reorder alerts */}
      <div className={styles.alertSection}>
        <div className={styles.sectionTitle}>
          Reorder Alerts
          {reorderItems.length > 0 && (
            <span className={styles.alertBadgeWarn}>{reorderItems.length}</span>
          )}
        </div>
        {reorderItems.length === 0 ? (
          <div className={styles.empty}>No items near reorder threshold.</div>
        ) : (
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span className={styles.colItem}>Item</span>
              <span className={styles.colType}>Type</span>
              <span className={styles.colStock}>Stock</span>
              <span className={styles.colMin}>Min Order</span>
            </div>
            {reorderItems.map(p => (
              <div key={p.id} className={styles.tableRow}>
                <span className={styles.colItem}>{p.description}</span>
                <span className={styles.colType}>
                  <span className={p.type === "Drug" ? styles.tagDrug : styles.tagSupply}>{p.type}</span>
                </span>
                <span className={`${styles.colStock} ${getStockClass(p.stock, p.minOrderLine)}`}>
                  {p.stock}
                </span>
                <span className={styles.colMin}>{p.minOrderLine}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current month summary cards */}
      <div className={styles.summaryCards}>
        <div className={styles.card}>
          <div className={styles.cardValue} style={{ color: "var(--accent-cyan)" }}>
            {currentMonth.total}
          </div>
          <div className={styles.cardLabel}>Total Used</div>
          <div className={styles.cardSub}>{months.length > 0 ? formatMonth(currentMonth.month) : "No data"}</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardValue} style={{ color: "var(--accent-orange)" }}>
            {currentMonth.drugs}
          </div>
          <div className={styles.cardLabel}>Drugs</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardValue} style={{ color: "var(--accent-purple)" }}>
            {currentMonth.supplies}
          </div>
          <div className={styles.cardLabel}>Supplies</div>
        </div>
      </div>

      {/* Monthly breakdown table */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Monthly Breakdown</div>
        {months.length === 0 ? (
          <div className={styles.empty}>No usage data yet.</div>
        ) : (
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span className={styles.colMonth}>Month</span>
              <span className={styles.colNum}>Drugs</span>
              <span className={styles.colNum}>Supplies</span>
              <span className={styles.colNum}>Total</span>
            </div>
            {months.map(m => (
              <div key={m.month}
                className={`${styles.tableRow} ${selectedMonth === m.month ? styles.tableRowActive : ""}`}
                onClick={() => setSelectedMonth(m.month)}>
                <span className={styles.colMonth}>{formatMonth(m.month)}</span>
                <span className={styles.colNum} style={{ color: "var(--accent-orange)" }}>{m.drugs}</span>
                <span className={styles.colNum} style={{ color: "var(--accent-purple)" }}>{m.supplies}</span>
                <span className={styles.colNum} style={{ color: "var(--accent-cyan)" }}>{m.total}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-item breakdown */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Item Details {selectedMonth && `— ${formatMonth(selectedMonth)}`}
        </div>
        {filteredItems.length === 0 ? (
          <div className={styles.empty}>No items for this period.</div>
        ) : (
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span className={styles.colItem}>Item</span>
              <span className={styles.colMfg}>Manufacturer</span>
              <span className={styles.colType}>Type</span>
              <span className={styles.colNum}>Qty Used</span>
            </div>
            {filteredItems.map((item, i) => (
              <div key={i} className={styles.tableRow}>
                <span className={styles.colItem}>{item.productName}</span>
                <span className={styles.colMfg}>{item.manufacturer}</span>
                <span className={styles.colType}>
                  <span className={item.type === "Drug" ? styles.tagDrug : styles.tagSupply}>
                    {item.type}
                  </span>
                </span>
                <span className={styles.colNum}>{item.quantity}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
